import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Briefcase, Users, Building2, Clock, CheckCircle2, XCircle, PauseCircle } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const COLORS = ["#1F7A63", "#E6B800", "#5C736A", "#2F9C80", "#B38F00", "#94A3B8", "#165C4B", "#F2D35C"];

function Card({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="ek-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#5C736A]"><Icon className={`w-4 h-4 ${tone}`} />{label}</div>
      <div className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`ek-card p-4 ${className}`}>
      <div className="font-semibold text-sm mb-3">{title}</div>
      <div className="h-60">{children}</div>
    </div>
  );
}

function HBar({ data }) {
  if (!data.length) return <div className="h-full grid place-items-center text-sm text-[#5C736A]">Aucune donnée</div>;
  return (
    <ResponsiveContainer>
      <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 10 }}>
        <XAxis type="number" allowDecimals={false} hide />
        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="value" name="Demandes" fill="#1F7A63" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ data }) {
  if (!data.some((d) => d.value)) return <div className="h-full grid place-items-center text-sm text-[#5C736A]">Aucune donnée</div>;
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function RegistrationStats({ onOpen, compact }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/admin/registrations/stats").then(({ data }) => setD(data)).catch((e) => setErr(formatError(e))); }, []);
  if (err) return <div className="ek-card p-4 text-sm text-red-600">{err}</div>;
  if (!d) return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skel h-24" />)}</div>;
  return (
    <div className="space-y-4" data-testid="registration-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Card icon={Briefcase} label="Directeurs" value={d.directeurs} tone="text-[#1F7A63]" testid="rs-directeurs" />
        <Card icon={Users} label="Enseignants" value={d.enseignants} tone="text-[#1F7A63]" testid="rs-enseignants" />
        <Card icon={Building2} label="Établissements" value={d.ecoles} tone="text-[#5C736A]" testid="rs-ecoles" />
        <Card icon={Clock} label="En attente" value={d.pending} tone="text-[#B38F00]" testid="rs-pending" />
        <Card icon={CheckCircle2} label="Approuvées" value={d.approved} tone="text-[#1F7A63]" testid="rs-approved" />
        <Card icon={XCircle} label="Refusées" value={d.rejected} tone="text-red-600" testid="rs-rejected" />
        <Card icon={PauseCircle} label="Suspendus" value={d.suspended} tone="text-slate-600" testid="rs-suspended" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title="Évolution des inscriptions par mois" className="lg:col-span-2">
          <ResponsiveContainer>
            <BarChart data={d.par_mois}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="demandes" name="Demandes" fill="#1F7A63" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Directeurs / Enseignants"><Donut data={d.par_profil} /></ChartCard>
      </div>
      {!compact && (
        <div className="grid lg:grid-cols-3 gap-4">
          <ChartCard title="Répartition par IA"><HBar data={d.par_ia} /></ChartCard>
          <ChartCard title="Répartition par IEF"><HBar data={d.par_ief} /></ChartCard>
          <ChartCard title="Type d'établissement"><Donut data={d.par_type} /></ChartCard>
        </div>
      )}
      <div className="ek-card p-0" data-testid="rs-latest">
        <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-sm">Dix dernières demandes</div>
        {d.dernieres.map((r) => (
          <button key={r.id} onClick={() => onOpen?.(r.id)} className="w-full text-left px-4 py-3 border-b border-[#E2E8F0] last:border-0 hover:bg-[#F6F8F9] flex items-center gap-3" data-testid="rs-latest-row">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{r.name} <span className="font-normal text-[#5C736A]">· {r.profil === "directeur" ? "Directeur" : "Enseignant"}</span></div>
              <div className="text-xs text-[#5C736A] truncate">{r.school?.nom} · {new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
            <StatusBadge status={r.status} />
          </button>
        ))}
        {!d.dernieres.length && <div className="px-4 py-6 text-sm text-[#5C736A] text-center">Aucune demande pour l&apos;instant.</div>}
      </div>
    </div>
  );
}
